import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getFirestore,
  onSnapshot,
  serverTimestamp,
  type Firestore,
} from "firebase/firestore";
import type {
  CollectionReferenceLike,
  DocumentReferenceLike,
  DocumentSnapshotLike,
  FirestoreLike,
  Unsubscribe,
} from "@orvis/data";
import type { FirebaseWebConfig } from "./config.js";

/**
 * Bridge from the Firebase Web SDK to `@orvis/data`'s `FirestoreLike`.
 *
 * Everything the app talks to Firestore through goes via this interface
 * — swap the implementation for `@capacitor-firebase/firestore` later
 * without touching a single call site.
 */
export function adaptFirestore(fs: Firestore): FirestoreLike {
  return {
    collection(name: string): CollectionReferenceLike {
      return {
        doc(id: string): DocumentReferenceLike {
          const ref = doc(fs, name, id);
          return {
            async get(): Promise<DocumentSnapshotLike> {
              const snap = await getDoc(ref);
              return { exists: snap.exists(), data: () => snap.data() };
            },
            onSnapshot(next, error): Unsubscribe {
              return onSnapshot(
                ref,
                (snap) => next({ exists: snap.exists(), data: () => snap.data() }),
                error
              );
            },
          };
        },
      };
    },
  };
}

export interface FirebaseBoot {
  app: FirebaseApp;
  firestore: Firestore;
  db: FirestoreLike;
}

let cached: FirebaseBoot | undefined;

/**
 * Initialize Firebase once per browser session. Safe to call multiple
 * times — returns the cached boot on subsequent calls.
 */
export function bootFirebase(config: FirebaseWebConfig): FirebaseBoot {
  if (cached) return cached;
  const app = initializeApp(config);
  const firestore = getFirestore(app);
  cached = { app, firestore, db: adaptFirestore(firestore) };
  return cached;
}

/**
 * Append a document to a create-only collection (today: `reports`). The
 * server timestamp lands in `createdAt`, which firestore.rules requires.
 * Rejects when Firebase hasn't booted.
 */
export async function createDoc(collectionName: string, data: Record<string, unknown>): Promise<void> {
  if (!cached) throw new Error("firebase not booted");
  await addDoc(collection(cached.firestore, collectionName), { ...data, createdAt: serverTimestamp() });
}
